import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Upload, Camera } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/I18nContext";
import { useInventory } from "@/lib/inventory";
import {
  useCreateListing,
  requestUploadUrl,
  uploadPhotoToTigris,
} from "@/lib/market";
import { useCreateAuction, AUCTION_DURATIONS } from "@/lib/auctions";

// Phase 22a/22b — CreateListingModal.
// Pre-filled from drag-drop payload (number + optional grade). User picks
// EITHER a fixed price (Phase 22a MatchMarket path) OR auction parameters
// (Phase 22b MatchBid path). Photo upload degrades gracefully to
// text-only when Tigris returns 503.

const GRADES = ["M", "NM", "EX", "G", "F", "HW"];
const DURATIONS = [3, 7, 14];
const MAX_PHOTOS = 5;
const MAX_FILE_MB = 5;
const MODE_FIXED = "fixed";
const MODE_AUCTION = "auction";

const inputCx =
  "w-full h-11 bg-[#121212] border border-white/10 rounded-md px-3 text-white placeholder:text-white/30 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A]";
const labelCx =
  "block text-xs font-medium text-white/80 mb-1.5 tracking-wide uppercase font-body";
const primaryBtnCx =
  "h-11 px-5 rounded-md font-semibold text-sm tracking-wide bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black hover:opacity-95 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2";
const secondaryBtnCx =
  "h-11 px-4 rounded-md font-medium text-sm tracking-wide bg-transparent border border-white/15 text-white hover:border-white/40 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2";

export default function CreateListingModal({ open, onOpenChange, prefill, initialMode = MODE_FIXED }) {
  const { t } = useI18n();
  const { data: inv } = useInventory();
  const createListing = useCreateListing();
  const createAuction = useCreateAuction();

  const [mode, setMode] = useState(initialMode);
  const [number, setNumber] = useState("");
  const [grade, setGrade] = useState("NM");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState(7);
  // Auction-only inputs
  const [startingPrice, setStartingPrice] = useState("");
  const [reservePrice, setReservePrice] = useState("");
  const [minIncrement, setMinIncrement] = useState("5");
  const [durationHours, setDurationHours] = useState(24);
  const [description, setDescription] = useState("");
  const [photoKeys, setPhotoKeys] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tigrisUnavailable, setTigrisUnavailable] = useState(false);
  const [error, setError] = useState("");

  // Reset every time the modal opens with a fresh prefill.
  useEffect(() => {
    if (!open) return;
    setError("");
    setMode(initialMode);
    setPrice("");
    setStartingPrice("");
    setReservePrice("");
    setMinIncrement("5");
    setDurationHours(24);
    setDescription("");
    setDuration(7);
    setPhotoKeys([]);
    setPhotoPreviews([]);
    setUploading(false);
    setTigrisUnavailable(false);
    if (prefill && Number.isFinite(prefill.number)) {
      setNumber(String(prefill.number));
      const g = prefill.grade;
      const preGrade =
        g && GRADES.includes(g)
          ? g
          : (inv?.sticker_grades && inv.sticker_grades[String(prefill.number)]) ||
            "NM";
      setGrade(preGrade);
    } else {
      setNumber("");
      setGrade("NM");
    }
  }, [open, prefill, inv?.sticker_grades, initialMode]);

  const isDuplicate = useMemo(() => {
    const n = parseInt(number, 10);
    if (!Number.isFinite(n)) return false;
    return (inv?.duplicates || []).includes(n);
  }, [number, inv?.duplicates]);

  const priceInt = parseInt(price, 10);
  const priceValid =
    Number.isFinite(priceInt) && priceInt >= 1 && priceInt <= 999_999;
  const startInt = parseInt(startingPrice, 10);
  const startValid =
    Number.isFinite(startInt) && startInt >= 1 && startInt <= 999_999;
  const reserveInt = reservePrice === "" ? null : parseInt(reservePrice, 10);
  const reserveValid =
    reserveInt === null ||
    (Number.isFinite(reserveInt) &&
      reserveInt >= startInt &&
      reserveInt <= 999_999);
  const incInt = parseInt(minIncrement, 10);
  const incValid = Number.isFinite(incInt) && incInt >= 1 && incInt <= 1000;

  const canSubmit =
    number !== "" &&
    GRADES.includes(grade) &&
    isDuplicate &&
    (mode === MODE_FIXED
      ? priceValid
      : startValid && reserveValid && incValid);

  const onPickPhotos = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []).slice(
        0,
        MAX_PHOTOS - photoKeys.length
      );
      if (files.length === 0) return;
      setUploading(true);
      try {
        for (const file of files) {
          if (file.size > MAX_FILE_MB * 1024 * 1024) {
            toast.error(
              t("market.photo_too_large", "Photo exceeds 5MB — pick a smaller file.")
            );
            continue;
          }
          const ct =
            file.type === "image/jpeg" || file.type === "image/jpg"
              ? "image/jpeg"
              : file.type === "image/webp"
              ? "image/webp"
              : "image/png";
          try {
            const { upload_url, object_key } = await requestUploadUrl(
              file.name,
              ct
            );
            await uploadPhotoToTigris(file, upload_url, ct);
            setPhotoKeys((p) => [...p, object_key]);
            const previewUrl = URL.createObjectURL(file);
            setPhotoPreviews((p) => [...p, previewUrl]);
          } catch (err) {
            const status = err?.response?.status;
            if (status === 503) {
              setTigrisUnavailable(true);
              toast.info(
                t(
                  "market.photo_upload_unavailable",
                  "Photo uploads are unavailable right now. Text-only listings still work."
                )
              );
              break;
            }
            toast.error(err?.response?.data?.detail?.message || err.message);
          }
        }
      } finally {
        setUploading(false);
        // clear the input so the same file can be re-picked
        try {
          e.target.value = "";
        } catch (_) {}
      }
    },
    [photoKeys.length, t]
  );

  const removePhoto = useCallback((idx) => {
    setPhotoKeys((p) => p.filter((_, i) => i !== idx));
    setPhotoPreviews((p) => {
      try {
        URL.revokeObjectURL(p[idx]);
      } catch (_) {}
      return p.filter((_, i) => i !== idx);
    });
  }, []);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canSubmit) return;
      setError("");
      try {
        if (mode === MODE_AUCTION) {
          await createAuction.mutateAsync({
            number: parseInt(number, 10),
            grade,
            starting_price: startInt,
            reserve_price: reserveInt,
            min_increment: incInt,
            duration_hours: durationHours,
            description: description.trim() || null,
            photo_keys: photoKeys,
          });
          toast.success(
            t("matchbid.created_toast", "Your auction is live.")
          );
        } else {
          await createListing.mutateAsync({
            number: parseInt(number, 10),
            grade,
            price: priceInt,
            duration_days: duration,
            description: description.trim() || null,
            photo_keys: photoKeys,
          });
          toast.success(t("market.listing_created", "Your listing is live."));
        }
        // Cleanup blob URLs before close
        photoPreviews.forEach((u) => {
          try {
            URL.revokeObjectURL(u);
          } catch (_) {}
        });
        onOpenChange(false);
      } catch (err) {
        const data = err?.response?.data?.detail;
        const msg =
          (data && typeof data === "object" && (data.message || data.error_code)) ||
          err.message;
        setError(msg);
      }
    },
    [
      canSubmit,
      mode,
      createListing,
      createAuction,
      number,
      grade,
      priceInt,
      startInt,
      reserveInt,
      incInt,
      durationHours,
      duration,
      description,
      photoKeys,
      photoPreviews,
      onOpenChange,
      t,
    ]
  );

  useEffect(() => {
    // Release object URLs on unmount.
    return () => {
      photoPreviews.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) return null;

  return createPortal(
    <div
      role="presentation"
      onClick={() => onOpenChange(false)}
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="create-listing-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("market.create_title", "Create Listing")}
        data-testid="create-listing-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] max-h-[92vh] bg-[#0A0A0A] border border-white/10 rounded-xl shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2
            data-testid="create-listing-title"
            className="font-display text-lg font-semibold text-white"
          >
            {mode === MODE_AUCTION
              ? t("matchbid.create_title", "Start Auction")
              : t("market.create_title", "Create Listing")}
          </h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="create-listing-close"
            aria-label="Close"
            className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-white/5 text-white/70 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Mode toggle (Phase 22b) */}
        <div
          role="tablist"
          aria-label="Listing mode"
          data-testid="create-listing-mode-tabs"
          className="flex border-b border-white/10"
        >
          <button
            type="button"
            role="tab"
            data-testid="create-listing-mode-fixed"
            aria-selected={mode === MODE_FIXED}
            onClick={() => setMode(MODE_FIXED)}
            className={
              "flex-1 min-h-[40px] px-4 py-2 text-xs font-medium tracking-wide transition-colors " +
              (mode === MODE_FIXED
                ? "text-white border-b-2 border-[#E5B83A] bg-[#E5B83A]/5"
                : "text-[#A1A1AA] hover:text-white")
            }
          >
            {t("matchbid.mode_fixed", "Fixed price")}
          </button>
          <button
            type="button"
            role="tab"
            data-testid="create-listing-mode-auction"
            aria-selected={mode === MODE_AUCTION}
            onClick={() => setMode(MODE_AUCTION)}
            className={
              "flex-1 min-h-[40px] px-4 py-2 text-xs font-medium tracking-wide transition-colors " +
              (mode === MODE_AUCTION
                ? "text-white border-b-2 border-[#E5B83A] bg-[#E5B83A]/5"
                : "text-[#A1A1AA] hover:text-white")
            }
          >
            {t("matchbid.mode_auction", "Auction")}
          </button>
        </div>

        <form
          onSubmit={submit}
          className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
          noValidate
        >
          <div>
            <label htmlFor="cl-number" className={labelCx}>
              {t("market.number_label", "Sticker Number (1–650)")}
            </label>
            <input
              id="cl-number"
              type="number"
              min={1}
              max={650}
              step={1}
              value={number}
              data-testid="create-listing-number"
              onChange={(e) => setNumber(e.target.value.replace(/[^0-9]/g, ""))}
              className={inputCx}
              readOnly={!!prefill}
            />
            {number && !isDuplicate ? (
              <p className="mt-1.5 text-xs text-[#FF6B6B] font-body">
                {t(
                  "market.not_a_duplicate",
                  "You can only list stickers already in your Duplicates."
                )}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="cl-grade" className={labelCx}>
              {t("market.grade_label", "Condition Grade")}
            </label>
            <select
              id="cl-grade"
              data-testid="create-listing-grade"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className={inputCx + " appearance-none pr-8"}
            >
              {GRADES.map((code) => (
                <option
                  key={code}
                  value={code}
                  className="bg-[#121212] text-white"
                >
                  {t(`figuscan.grade_${code}_label`, code)}
                </option>
              ))}
            </select>
          </div>

          {mode === MODE_FIXED ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="cl-price" className={labelCx}>
                  {t("market.price_label", "Price (FiguTokens)")}
                </label>
                <input
                  id="cl-price"
                  type="number"
                  min={1}
                  max={999999}
                  step={1}
                  value={price}
                  data-testid="create-listing-price"
                  onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="e.g. 50"
                  className={inputCx}
                />
              </div>
              <div>
                <label htmlFor="cl-duration" className={labelCx}>
                  {t("market.duration_label", "Duration")}
                </label>
                <select
                  id="cl-duration"
                  data-testid="create-listing-duration"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                  className={inputCx + " appearance-none pr-8"}
                >
                  {DURATIONS.map((d) => (
                    <option key={d} value={d} className="bg-[#121212] text-white">
                      {d} {t("market.days", "days")}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="cl-start" className={labelCx}>
                    {t("matchbid.starting_price_label", "Starting price (FT)")}
                  </label>
                  <input
                    id="cl-start"
                    type="number"
                    min={1}
                    max={999999}
                    step={1}
                    value={startingPrice}
                    data-testid="create-listing-starting-price"
                    onChange={(e) =>
                      setStartingPrice(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    placeholder="e.g. 10"
                    className={inputCx}
                  />
                </div>
                <div>
                  <label htmlFor="cl-inc" className={labelCx}>
                    {t("matchbid.min_increment_label", "Min increment")}
                  </label>
                  <input
                    id="cl-inc"
                    type="number"
                    min={1}
                    max={1000}
                    step={1}
                    value={minIncrement}
                    data-testid="create-listing-min-increment"
                    onChange={(e) =>
                      setMinIncrement(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    placeholder="5"
                    className={inputCx}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="cl-reserve" className={labelCx}>
                    {t("matchbid.reserve_price_label", "Reserve (optional)")}
                  </label>
                  <input
                    id="cl-reserve"
                    type="number"
                    min={1}
                    max={999999}
                    step={1}
                    value={reservePrice}
                    data-testid="create-listing-reserve-price"
                    onChange={(e) =>
                      setReservePrice(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    placeholder={t("matchbid.reserve_placeholder", "no reserve")}
                    className={inputCx}
                  />
                  {reservePrice && !reserveValid ? (
                    <p className="mt-1.5 text-xs text-[#FF6B6B] font-body">
                      {t(
                        "matchbid.err_reserve_below_starting",
                        "Reserve must be at least the starting price."
                      )}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label htmlFor="cl-hours" className={labelCx}>
                    {t("matchbid.duration_label", "Auction length")}
                  </label>
                  <select
                    id="cl-hours"
                    data-testid="create-listing-duration-hours"
                    value={durationHours}
                    onChange={(e) => setDurationHours(parseInt(e.target.value, 10))}
                    className={inputCx + " appearance-none pr-8"}
                  >
                    {AUCTION_DURATIONS.map((d) => (
                      <option
                        key={d.hours}
                        value={d.hours}
                        className="bg-[#121212] text-white"
                      >
                        {t(d.key, `${d.hours}h`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          <div>
            <label htmlFor="cl-desc" className={labelCx}>
              {t("market.description_label", "Description (optional)")}
            </label>
            <textarea
              id="cl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              rows={3}
              data-testid="create-listing-description"
              className={inputCx + " h-auto py-2 resize-none"}
              placeholder={t(
                "market.description_placeholder",
                "Any handling notes for the buyer?"
              )}
            />
          </div>

          <div>
            <label className={labelCx}>
              {t("market.photos_label", "Photos (optional, max 5)")}
            </label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {photoPreviews.map((src, idx) => (
                <div
                  key={idx}
                  data-testid={`create-listing-photo-${idx}`}
                  className="relative aspect-square rounded-md overflow-hidden border border-white/10"
                >
                  <img
                    src={src}
                    alt="preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 border border-white/20 text-white text-xs flex items-center justify-center"
                    aria-label="Remove photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photoKeys.length < MAX_PHOTOS ? (
                <label
                  data-testid="create-listing-photo-input-wrap"
                  className={
                    "aspect-square rounded-md border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-white/60 cursor-pointer hover:border-[#E5B83A]/60 transition-colors " +
                    (tigrisUnavailable ? "opacity-40 cursor-not-allowed" : "")
                  }
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      <span className="mt-1 text-[10px] font-body">
                        {t("market.add_photo", "Add photo")}
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    disabled={tigrisUnavailable || uploading}
                    onChange={onPickPhotos}
                    data-testid="create-listing-photo-input"
                    className="hidden"
                  />
                </label>
              ) : null}
            </div>
            {tigrisUnavailable ? (
              <p
                data-testid="create-listing-tigris-warning"
                className="text-[11px] text-[#FFB86B] font-body"
              >
                {t(
                  "market.photo_upload_unavailable",
                  "Photo uploads are unavailable right now. Text-only listings still work."
                )}
              </p>
            ) : null}
          </div>

          {error ? (
            <div
              data-testid="create-listing-error"
              role="alert"
              className="rounded-md border-2 border-[#FF0000] bg-[#3B0000]/40 px-3 py-2 text-sm text-[#FFAAAA] font-body"
            >
              {error}
            </div>
          ) : null}
        </form>

        <footer className="px-5 py-4 border-t border-white/10 flex items-center gap-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="create-listing-cancel"
            className={secondaryBtnCx + " flex-1"}
          >
            {t("market.cancel", "Cancel")}
          </button>
          <button
            type="submit"
            onClick={submit}
            disabled={
              !canSubmit || createListing.isPending || createAuction.isPending
            }
            data-testid="create-listing-submit"
            className={primaryBtnCx + " flex-1"}
          >
            {createListing.isPending || createAuction.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {mode === MODE_AUCTION
              ? t("matchbid.submit", "Start auction")
              : t("market.submit", "Create listing")}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
